import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  CHILDREN,
  CRASHLYTICS_OVERSEAS_ROW,
  CRASHLYTICS_ROW,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  OVERSEAS_REFUSAL,
  PROCESSOR_HEADERS,
  R2_ITEMS_SUPPORT,
  R2_RETENTION_SUPPORT,
  REMEDIES,
  RETENTION_HEADERS,
  REVENUECAT_OVERSEAS_ROW,
  REVENUECAT_ROW,
  SECURITY,
  SPONSOR_ITEM_AUTO,
  SPONSOR_NOTE,
  SPONSOR_PURPOSE,
  SPONSOR_RETENTION_ROW,
  SUPPORT_ITEM_AUTO,
  SUPPORT_ITEM_INPUT,
  SUPPORT_NOTE,
  SUPPORT_PURPOSE,
  SUPPORT_RETENTION_ROW,
  changes,
  cloudflareOverseasRow,
  cloudflareRow,
  noAutomaticCollection,
  rights,
  table,
} from './common'

/**
 * Easy Air Screen 개인정보 처리방침.
 *
 * 백엔드 서버가 없다 — 화면 데이터는 같은 로컬 네트워크 안에서만 오가고, 밖으로 나가는 것은
 * Firebase Crashlytics 진단 정보 하나뿐이다. 이 구조가 바뀌면 문서를 먼저 고친다
 * (docs/privacy-policy.md).
 */

const EFFECTIVE_DATE = 'August 20, 2026'
const LAST_REVISED = 'August 26, 2026'
const CRASH_RETENTION = '90 days'

const SECTIONS: readonly PolicySection[] = [
  {
    heading: 'Article 1. Purposes of processing personal data',
    paragraphs: [
      'The Operator processes personal data for the following purposes only. Should a purpose change, the Operator takes the steps required by Article 18 of the Personal Information Protection Act, including obtaining separate consent.',
    ],
    bullets: [
      'Keeping the service reliable — finding and fixing the places where the app crashes or errors',
      SUPPORT_PURPOSE,
      SPONSOR_PURPOSE,
    ],
  },
  {
    heading: 'Article 2. Personal data processed',
    paragraphs: [
      'The service works without an account, and the items below are everything the Operator processes.',
    ],
    bullets: [
      '[Collected automatically] App diagnostics — Firebase Crashlytics installation identifier and Firebase installation ID, screen and action names, app version and build, device model, operating system and device state, error messages, stack traces, and crash traces',
      SUPPORT_ITEM_INPUT,
      SUPPORT_ITEM_AUTO,
      SPONSOR_ITEM_AUTO,
    ],
    note: 'No name, phone number, location, screen content, or mirrored video is collected. An email address is collected only where you type one into the support page yourself so that a reply can be sent, and a photograph only where you attach one to a support message yourself. Neither sensitive data under Article 23 nor unique identifying information under Article 24 of the Personal Information Protection Act is collected.',
  },
  {
    heading: 'Article 3. Handling of screen data, and device permissions',
    paragraphs: [
      'Screen mirroring is carried out entirely on your own devices. The connection between the phone and the computer stays within the same local network and passes through no external server. The core mirroring feature needs no internet connection; the internet is used only to send error diagnostics.',
      'Separately from the core feature, a message you send yourself from the Support screen in Settings is transmitted to the Operator’s server. It contains no screen content or mirrored video: only the text you wrote, together with your app version, device model, operating system version, and language setting (Articles 2 and 4).',
    ],
    bullets: [
      'Android location permission (ACCESS_FINE_LOCATION) — required by the operating system on Android 12 and later in order to find devices on the local network. The Operator neither collects nor stores location data',
      'Android nearby devices permission (NEARBY_WIFI_DEVICES) — used only to find computers on the same network',
      'iOS local network permission — asked once, so the app can talk to a computer on the local network',
    ],
    note: 'None of these permissions is used to collect your location or any other personal data. Attaching a photograph to a support message needs no permission: the picker belongs to the operating system, and the app receives only the photographs you choose there.',
  },
  {
    heading: 'Article 4. Processing and retention periods',
    table: table(RETENTION_HEADERS, [
      ['App diagnostics', `${CRASH_RETENTION} from collection`, 'Your consent (processor retention cycle)'],
      [...SUPPORT_RETENTION_ROW],
      [...SPONSOR_RETENTION_ROW],
    ]),
    note: `The Operator carries out no transaction that would create a retention duty under the Act on Consumer Protection in Electronic Commerce or similar legislation, so no personal data is kept separately under any statute. Deleting the app stops any further transmission of diagnostics. ${SUPPORT_NOTE} ${SPONSOR_NOTE}`,
  },
  {
    heading: 'Article 5. Provision of personal data to third parties',
    paragraphs: [
      'The Operator processes personal data only within the purposes set out in Article 1 and provides it to third parties only with your consent or where Article 17 or Article 18 of the Personal Information Protection Act applies. Personal data is never sold.',
    ],
  },
  {
    heading: 'Article 6. Entrustment of personal data processing',
    paragraphs: [
      'To keep the service reliable, the Operator entrusts personal data processing as follows.',
    ],
    table: table(PROCESSOR_HEADERS, [
      [...CRASHLYTICS_ROW],
      cloudflareRow(R2_ITEMS_SUPPORT, R2_RETENTION_SUPPORT),
      [...REVENUECAT_ROW],
    ]),
    note: 'In each processing agreement, as required by Article 26 of the Personal Information Protection Act, the Operator sets out in writing the prohibition on processing beyond the stated purpose, technical and administrative safeguards, restrictions on sub-processing, supervision, and liability for damages, and supervises whether the processor handles personal data safely. If what is entrusted changes, this policy is updated and notice given in advance.',
  },
  {
    heading: 'Article 7. Transfer of personal data abroad',
    paragraphs: [
      'The processor named in Article 6 is located outside Korea, and the entrustment transfers personal data abroad as follows.',
    ],
    table: table(OVERSEAS_HEADERS, [
      [...CRASHLYTICS_OVERSEAS_ROW],
      cloudflareOverseasRow(R2_ITEMS_SUPPORT, R2_RETENTION_SUPPORT),
      [...REVENUECAT_OVERSEAS_ROW],
    ]),
    note: OVERSEAS_REFUSAL,
  },
  DISPOSAL,
  rights(
    [
      'Deleting the app — removing the app stops any further transmission of diagnostics',
      'Deleting a support message — send us the reference number shown on the support page at the email address below and that message is deleted',
    ],
    'the text of support messages and app diagnostics',
  ),
  noAutomaticCollection('No analytics or user tracking is carried out for advertising purposes.'),
  {
    ...CHILDREN,
    note: 'The service asks children for no additional personal data. If the app runs into an error, however, the same diagnostics are collected automatically as for any other user.',
  },
  SECURITY,
  OFFICER,
  REMEDIES,
  changes(EFFECTIVE_DATE, [
    'August 26, 2026 — the photographs attached to support messages moved from the Operator’s own server disk to Cloudflare R2 object storage. Articles 6 and 7 were amended accordingly: image files are now held by a processor outside Korea. What is collected, why, and how long it is kept did not change',
    'August 26, 2026 — the support screen moved out of the app to a page at cottonray.com/support which the app opens inside itself. What is collected, why, and how long it is kept did not change',
    'August 24, 2026 — updated the purposes, items collected, and retention periods following the addition of photo attachments (up to three, chosen by you) and an optional email address to the in-app support screen, which the Operator uses to send a single reply from no-reply@cottonray.com',
  'August 24, 2026 — updated the purposes, items collected, retention periods, entrustment, and notice of transfers abroad following the introduction of the optional “buy the developer a coffee” sponsorship (in-app purchase; receipts verified by RevenueCat)',
    'August 15, 2026 — established (structured on Article 30 of the Personal Information Protection Act, including notice of transfers abroad)',
    'August 20, 2026 — updated the purposes, items collected, retention periods, and how to exercise your rights following the introduction of in-app support messages (feedback only, no contact details taken)',
    'August 19, 2026 — the site language changed to English and this policy was reissued in English. Nothing about how data is handled changed',
  ]),
]

export const EASY_AIR_SCREEN_POLICY: PolicyDocument = {
  slug: 'easy-air-screen',
  title: 'Easy Air Screen Privacy Policy',
  intro: `${OPERATOR}, establishes and publishes the following privacy policy for Easy Air Screen (“the service”) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it. The service runs over a local network between your own devices, with no backend server.`,
  effectiveDate: EFFECTIVE_DATE,
  lastRevisedDate: LAST_REVISED,
  sections: SECTIONS,
}
